import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAdminApprovalRequestsEvents, useAdminDashboardEvents } from '../../../contexts/AdminEventsProvider';
import { useAuth } from '../../../contexts/AuthProvider';
import {
  adminApprovalRequestsService,
  AdminApprovalRequestDetailView,
  AdminApprovalRequestView,
} from '../../../services/admin-approval-requests/admin-approval-requests.service';
import { approvalsService } from '../../../services/approvals/approvals.service';
import { employeesService, EmployeeView } from '../../../services/employees/employees.service';
import { adminPaymentsService, AdminPaymentlineView } from '../../../services/admin-payments/admin-payments.service';
import { adminSessionsService, AdminSessionAuditView } from '../../../services/admin-sessions/admin-sessions.service';
import { ordersService, OrderView } from '../../../services/orders/orders.service';
import { pastReceiptsService, SessionFilterOption } from '../../../services/past-receipts/past-receipts.service';
import { reportsService, ReportView, CategorySales, BestSellingProduct, MostCanceledProduct, BumpTimesSummary, ReportStationaryStationSummary } from '../../../services/reports/reports.service';
import { ApprovalMode, BarRow, BreakdownRow, DashboardSessionBlock, FilterMode, SectionKey, TotalsMode, UseDashboardPage } from '../shared/types/dashboard-page.types';
import {
  addDays,
  computeCancellationTotals,
  computeClosedOrdersSummary,
  computeGraphRows,
  computeOpenOrdersSummary,
  computePaymentByMethod,
  computeSameDaysComparison,
  computeTotalsCards,
  computeWaiterActionBreakdown,
  doesTenantAliasMatch,
  formatHeaderDay,
  formatWeekLabel,
  isSameTenant,
  normalizeTenantToken,
  startOfIsraeliWeek,
  sum,
} from '../shared/utils/dashboard-page.utils';

const APPROVAL_PAGE_SIZE = 10;
const WAITER_ACTION_PAGE_SIZE = 500;

const computeCountComparisonPct = (currentCount: number, baselineCount: number | null): number => {
  if (baselineCount === null) return 0;
  if (baselineCount === 0) return currentCount > 0 ? 100 : 0;
  return ((currentCount - baselineCount) / baselineCount) * 100;
};

// A window holding a session synthesized from filter options has no authoritative sales total.
const sumWindowSales = (rows: AdminSessionAuditView[], synthesizedIds: ReadonlySet<number>): number | null => (
  rows.some((row) => synthesizedIds.has(row.sessionId)) ? null : sum(rows.map((row) => row.salesTotal))
);

type ApprovalCountBaseline ={ approved: number | null; cancellations: number | null };
type ApprovalFilterCacheEntry = {
  scope: string;
  rows: AdminApprovalRequestView[];
  total: number;
};

export function useDashboardPage(): UseDashboardPage {
  const { tenantId } = useParams();
  const { availableTenants, selectedTenantId, user } = useAuth();
  const { revision: approvalRevision } = useAdminApprovalRequestsEvents();
  const { revision: dashboardRevision, lastTenantId: dashboardLastTenantId } = useAdminDashboardEvents();

  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  // SSE revisions re-run the same fetches, so the overlay is latched to the first completed load;
  // every later refresh updates the data in place.
  const initialLoadSettledRef = useRef(false);
  const isFetchingCoreData = loading || reportLoading;
  useEffect(() => {
    if (!isFetchingCoreData) {
      initialLoadSettledRef.current = true;
    }
  }, [isFetchingCoreData]);
  const [error, setError] = useState<string | null>(null);

  const [sessions, setSessions] = useState<AdminSessionAuditView[]>([]);
  const [synthesizedSessionIds, setSynthesizedSessionIds] = useState<ReadonlySet<number>>(new Set());
  const [openOrders, setOpenOrders] = useState<OrderView[]>([]);
  const [closedOrderDinersByCode, setClosedOrderDinersByCode] = useState<Record<string, number>>({});
  const [payments, setPayments] = useState<AdminPaymentlineView[]>([]);
  const [sessionScopedPayments, setSessionScopedPayments] = useState<AdminPaymentlineView[] | null>(null);
  const [approvals, setApprovals] = useState<AdminApprovalRequestView[]>([]);
  const [waiterActionApprovals, setWaiterActionApprovals] = useState<AdminApprovalRequestView[]>([]);
  const [approvalBaselineByBlockKey, setApprovalBaselineByBlockKey] = useState<Record<string, ApprovalCountBaseline>>({});
  const [report, setReport] = useState<ReportView | null>(null);
  const [reportsBySessionId, setReportsBySessionId] = useState<Record<number, ReportView>>({});
  const [categorySales, setCategorySales] = useState<CategorySales[]>([]);
  const [categorySalesBySessionId, setCategorySalesBySessionId] = useState<Record<number, CategorySales[]>>({});
  const [bestSellingProducts, setBestSellingProducts] = useState<BestSellingProduct[]>([]);
  const [bestSellingProductsBySessionId, setBestSellingProductsBySessionId] = useState<Record<number, BestSellingProduct[]>>({});
  const [mostCanceledProducts, setMostCanceledProducts] = useState<MostCanceledProduct[]>([]);
  const [mostCanceledProductsBySessionId, setMostCanceledProductsBySessionId] = useState<Record<number, MostCanceledProduct[]>>({});
  const [stationaryStationSummaries, setStationaryStationSummaries] = useState<ReportStationaryStationSummary[]>([]);
  const [stationaryStationSummariesBySessionId, setStationaryStationSummariesBySessionId] = useState<Record<number, ReportStationaryStationSummary[]>>({});
  const [bumpTimesSummary, setBumpTimesSummary] = useState<BumpTimesSummary>({ firstBumpAt: null, lastBumpAt: null });
  const [bumpTimesSummaryBySessionId, setBumpTimesSummaryBySessionId] = useState<Record<number, BumpTimesSummary>>({});
  const [reportRequestFailed, setReportRequestFailed] = useState(false);
  const [categorySalesRequestFailed, setCategorySalesRequestFailed] = useState(false);
  const [bestSellingRequestFailed, setBestSellingRequestFailed] = useState(false);
  const [mostCanceledRequestFailed, setMostCanceledRequestFailed] = useState(false);
  const [bumpTimesRequestFailed, setBumpTimesRequestFailed] = useState(false);
  const [cancelDetails, setCancelDetails] = useState<AdminApprovalRequestDetailView[]>([]);
  const [tenantSessionOptions, setTenantSessionOptions] = useState<SessionFilterOption[]>([]);

  const [filterMode, setFilterMode] = useState<FilterMode>('shift');
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [pendingStepRange, setPendingStepRange] = useState<[Date, Date] | null>(null);
  const pendingStepRangeRef = useRef<[Date, Date] | null>(null);
  const stepCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingStep = useCallback(() => {
    if (stepCommitTimerRef.current) {
      clearTimeout(stepCommitTimerRef.current);
      stepCommitTimerRef.current = null;
    }
    pendingStepRangeRef.current = null;
    setPendingStepRange(null);
  }, []);
  const [loadedSessionIds, setLoadedSessionIds] = useState<number[]>([]);
  const [totalsMode, setTotalsMode] = useState<TotalsMode>('ordersTotal');
  const [breakdownQuery, setBreakdownQuery] = useState('');
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>('all');
  const [requestedApprovalMode, setRequestedApprovalMode] = useState<ApprovalMode>('all');
  const [approvalPage, setApprovalPage] = useState(1);
  const [approvalTotal, setApprovalTotal] = useState(0);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [selectedBreakdownRow, setSelectedBreakdownRow] = useState<BreakdownRow | null>(null);
  const [approvalsActionBusy, setApprovalsActionBusy] = useState(false);
  const [managerOptions, setManagerOptions] = useState<EmployeeView[]>([]);
  const [selectedApproverName, setSelectedApproverName] = useState<string | null>(null);
  const [approvalsActionError, setApprovalsActionError] = useState<string | null>(null);
  const [approvalDetailsById, setApprovalDetailsById] = useState<Record<number, AdminApprovalRequestDetailView>>({});
  const approvalDecisionInFlightRef = useRef(false);
  const approvalLoadInFlightRef = useRef(false);
  const approvalQueryGenerationRef = useRef(0);
  const approvalFilterCacheRef = useRef<Partial<Record<ApprovalMode, ApprovalFilterCacheEntry>>>({});
  const waiterActionQueryGenerationRef = useRef(0);
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    topSummary: false,
    approvals: false,
    sales: false,
    paymentMethod: false,
    salesGraph: false,
    payments: false,
    refunds: false,
    cancellations: false,
    waiterIssues: false,
    categories: false,
    bestSelling: false,
    mostCancelled: false,
    stationaryStations: false,
    bumpTimes: false,
    loadPreviousShift: false,
  });

  const isManualSessionSelectionRef = useRef(false);
  const sortedSessionsRef = useRef<AdminSessionAuditView[]>([]);
  sortedSessionsRef.current = sessions;

  // Manual pick: freeze auto-follow so a deliberate historical shift isn't yanked to latest.
  const selectLoadedSessionIds = useCallback((ids: number[]) => {
    const latestSessionId = sortedSessionsRef.current[0]?.sessionId;
    const isFollowingLatest = ids.length === 1 && latestSessionId !== undefined && ids[0] === latestSessionId;
    isManualSessionSelectionRef.current = !isFollowingLatest;
    cancelPendingStep();
    setLoadedSessionIds(ids);
  }, [cancelPendingStep]);

  const tenantAliases = useMemo(() => {
    const selectedTenantOption = availableTenants.find((tenant) => tenant.tenantId === tenantId)
      ?? (selectedTenantId ? availableTenants.find((tenant) => tenant.tenantId === selectedTenantId) : undefined);

    const rawAliases = [
      tenantId ?? '',
      selectedTenantId ?? '',
      selectedTenantOption?.tenantId ?? '',
      selectedTenantOption?.companyName ?? '',
      user?.tenantId ?? '',
      ...(user?.tenantIds ?? []),
    ];

    return [...new Set(rawAliases.map((value) => normalizeTenantToken(value)).filter((value) => value.length > 0))];
  }, [availableTenants, selectedTenantId, tenantId, user?.tenantId, user?.tenantIds]);

  const dashboardTenantRevision = useMemo(() => {
    if (!tenantId || !dashboardLastTenantId) {
      return 0;
    }
    if (
      isSameTenant(dashboardLastTenantId, tenantId)
      || doesTenantAliasMatch(dashboardLastTenantId, tenantAliases)
    ) {
      return dashboardRevision;
    }
    return 0;
  }, [dashboardLastTenantId, dashboardRevision, tenantAliases, tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      const [sessionsResult, sessionOptionsResult, paymentsResult, openOrdersResult] = await Promise.allSettled([
        adminSessionsService.list({ page: 1, pageSize: 200, sortKey: 'openTime', sortDirection: 'desc' }),
        pastReceiptsService.listDashboardSessionFilterOptions(tenantId),
        adminPaymentsService.list({ page: 1, pageSize: 400, sortKey: 'updatedAt', sortDirection: 'desc' }),
        ordersService.listDashboardOpenOrders(tenantId),
      ]);

      if (sessionOptionsResult.status === 'fulfilled') {
        setTenantSessionOptions(sessionOptionsResult.value);
      } else {
        setTenantSessionOptions([]);
      }

      const tenantPayments = paymentsResult.status === 'fulfilled'
        ? paymentsResult.value.rows.filter(
          (row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases),
        )
        : [];

      const paymentSessionAggregates = tenantPayments.reduce((acc, row) => {
        if (row.status !== 'done') {
          return acc;
        }
        const current = acc.get(row.sessionId) ?? {
          salesTotal: 0,
          cashTotal: 0,
          creditTotal: 0,
          orderIds: new Set<number>(),
        };
        if (row.orderStatus === 'closed' && row.orderId > 0) {
          current.salesTotal += row.baseAmount;
          current.orderIds.add(row.orderId);
        }
        if (row.method === 'cash') {
          current.cashTotal += row.amount;
        }
        if (row.method === 'credit') {
          current.creditTotal += row.amount;
        }
        acc.set(row.sessionId, current);
        return acc;
      }, new Map<number, {
        salesTotal: number;
        cashTotal: number;
        creditTotal: number;
        orderIds: Set<number>;
      }>());

      const roundToTenth = (value: number): number => Math.round(value * 10) / 10;

      const sortedSessions = sessionsResult.status === 'fulfilled'
        ? [...sessionsResult.value.rows].sort((a, b) => new Date(b.openTime).getTime() - new Date(a.openTime).getTime())
        : [];

      const allowedSessionIds = sessionOptionsResult.status === 'fulfilled'
        ? new Set(sessionOptionsResult.value.map((option) => option.id).filter((value) => Number.isFinite(value)))
        : new Set<number>();

      const resolvedSessions = allowedSessionIds.size > 0
        ? sortedSessions.filter((row) => allowedSessionIds.has(row.sessionId))
        : sortedSessions.filter((row) => {
          if (isSameTenant(row.tenantId, tenantId)) {
            return true;
          }
          return doesTenantAliasMatch(row.tenantId, tenantAliases) || doesTenantAliasMatch(row.companyName, tenantAliases);
        });

      const fallbackSessionsFromTenantOptions = sessionOptionsResult.status === 'fulfilled'
        ? [...sessionOptionsResult.value]
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .map<AdminSessionAuditView>((option) => {
            const aggregate = paymentSessionAggregates.get(option.id);
            return {
              sessionId: option.id,
              tenantId,
              companyName: '',
              sessionName: option.name,
              openingUser: null,
              openTime: option.date,
              closeTime: option.date,
              state: option.state,
              autoClosed: null,
              orderCount: aggregate?.orderIds.size ?? 0,
              salesTotal: roundToTenth(aggregate?.salesTotal ?? 0),
              cashTotal: roundToTenth(aggregate?.cashTotal ?? 0),
              creditTotal: roundToTenth(aggregate?.creditTotal ?? 0),
              closedByUser: null,
            };
          })
        : [];

      const resolvedSessionIds = new Set(resolvedSessions.map((row) => row.sessionId));
      const synthesizedSessions = fallbackSessionsFromTenantOptions
        .filter((row) => !resolvedSessionIds.has(row.sessionId));
      const finalSessions = [...resolvedSessions, ...synthesizedSessions]
        .sort((a, b) => new Date(b.openTime).getTime() - new Date(a.openTime).getTime());

      setSessions(finalSessions);
      setSynthesizedSessionIds(new Set(synthesizedSessions.map((row) => row.sessionId)));
      setLoadedSessionIds((previous) => {
        const validPrevious = previous.filter((sessionId) => finalSessions.some((row) => row.sessionId === sessionId));
        const latestSessionId = finalSessions[0]?.sessionId;
        // Follow newest active session unless the user deliberately picked an older shift.
        if (!isManualSessionSelectionRef.current && latestSessionId !== undefined) {
          return [latestSessionId];
        }
        if (validPrevious.length > 0) {
          return validPrevious;
        }
        if (latestSessionId !== undefined) {
          return [latestSessionId];
        }
        return [];
      });

      if (paymentsResult.status === 'fulfilled') {
        setPayments(tenantPayments);
      } else {
        setPayments([]);
      }

      if (openOrdersResult.status === 'fulfilled') {
        setOpenOrders(
          openOrdersResult.value.filter(
            (row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases),
          ),
        );
      } else {
        setOpenOrders([]);
      }

      const hasFailure = [sessionsResult, paymentsResult, openOrdersResult]
        .some((result) => result.status === 'rejected');
      if (hasFailure) {
        setError('Some dashboard datasets failed to load. Showing available sections only.');
      }

      setLoading(false);
    };

    void loadDashboard();
  }, [approvalRevision, dashboardTenantRevision, tenantAliases, tenantId]);

  useEffect(() => {
    if (!isFilterDialogOpen) {
      return;
    }

    const currentSessionId = loadedSessionIds[0]
      ?? tenantSessionOptions[0]?.id
      ?? sessions[0]?.sessionId
      ?? null;
    setPendingSessionId(currentSessionId ? String(currentSessionId) : null);
  }, [isFilterDialogOpen, loadedSessionIds, sessions, tenantSessionOptions]);

  useEffect(() => {
    if (!isFilterDialogOpen) {
      return;
    }
    setRangeStart((current) => current ?? new Date());
    setRangeEnd((current) => current ?? new Date());
  }, [isFilterDialogOpen]);

  useEffect(() => {
    if (!tenantId) {
      return;
    }
    const loadManagers = async () => {
      try {
        const employees = await employeesService.listActive(tenantId);
        const managers = employees.filter((employee) => employee.role === 'manager');
        setManagerOptions(managers);
      } catch {
        setManagerOptions([]);
      }
    };
    void loadManagers();
  }, [tenantId]);

  const activeSessions = useMemo(() => {
    if (!tenantId) {
      return [];
    }
    if (filterMode === 'shift') {
      return loadedSessionIds
        .map((sessionId) => {
          const matchedSession = sessions.find((row) => row.sessionId === sessionId);
          if (matchedSession) {
            return matchedSession;
          }

          const option = tenantSessionOptions.find((entry) => entry.id === sessionId);
          if (!option) {
            return null;
          }

          return {
            sessionId: option.id,
            tenantId,
            companyName: '',
            sessionName: option.name,
            openingUser: null,
            openTime: option.date,
            closeTime: option.date,
            state: option.state,
            autoClosed: null,
            orderCount: 0,
            salesTotal: 0,
            cashTotal: 0,
            creditTotal: 0,
          };
        })
        .filter((row): row is AdminSessionAuditView => row !== null);
    }
    if (!rangeStart || !rangeEnd) {
      return [];
    }
    const rangeStartMs = new Date(rangeStart).setHours(0, 0, 0, 0);
    const rangeEndMs = new Date(rangeEnd).setHours(23, 59, 59, 999);
    const sessionById = new Map(sessions.map((row) => [row.sessionId, row]));
    return tenantSessionOptions
      .filter((option) => {
        const openMs = new Date(option.date).getTime();
        return openMs >= rangeStartMs && openMs <= rangeEndMs;
      })
      .map((option) => sessionById.get(option.id) ?? {
        sessionId: option.id,
        tenantId,
        companyName: '',
        sessionName: option.name,
        openingUser: null,
        openTime: option.date,
        closeTime: option.date,
        state: option.state,
        autoClosed: null,
        orderCount: 0,
        salesTotal: 0,
        cashTotal: 0,
        creditTotal: 0,
        closedByUser: null,
      });
  }, [filterMode, loadedSessionIds, rangeEnd, rangeStart, sessions, tenantId, tenantSessionOptions]);

  useEffect(() => {
    const generation = approvalQueryGenerationRef.current + 1;
    approvalQueryGenerationRef.current = generation;
    approvalLoadInFlightRef.current = false;
    if (!tenantId || activeSessions.length === 0) {
      approvalFilterCacheRef.current = {};
      setApprovals([]);
      setApprovalPage(1);
      setApprovalTotal(0);
      return;
    }
    const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
    const sessionIds = activeSessions.map((session) => String(session.sessionId));
    const cacheScope = [tenantId, sessionIds.join(','), approvalRevision, dashboardTenantRevision].join('|');
    const cached = approvalFilterCacheRef.current[requestedApprovalMode];
    if (cached?.scope === cacheScope) {
      setApprovals(cached.rows);
      setApprovalPage(1);
      setApprovalTotal(cached.total);
      setApprovalMode(requestedApprovalMode);
      return;
    }
    const loadApprovals = async () => {
      try {
        const page = await adminApprovalRequestsService.list({
          page: 1,
          pageSize: APPROVAL_PAGE_SIZE,
          session: sessionIds,
          status: requestedApprovalMode === 'all' ? undefined : [requestedApprovalMode],
        });
        const tenantApprovals = page.rows
          .filter((row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases))
          .filter((row) => activeSessionIds.has(row.sessionId))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        if (approvalQueryGenerationRef.current !== generation) return;
        approvalFilterCacheRef.current[requestedApprovalMode] = {
          scope: cacheScope,
          rows: tenantApprovals,
          total: page.total,
        };
        setApprovals(tenantApprovals);
        setApprovalPage(1);
        setApprovalTotal(page.total);
        setApprovalMode(requestedApprovalMode);
      } catch {
        if (approvalQueryGenerationRef.current !== generation) return;
      }
    };

    void loadApprovals();
  }, [activeSessions, approvalRevision, dashboardTenantRevision, requestedApprovalMode, tenantAliases, tenantId]);

  useEffect(() => {
    const generation = waiterActionQueryGenerationRef.current + 1;
    waiterActionQueryGenerationRef.current = generation;
    if (!tenantId || activeSessions.length === 0) {
      setWaiterActionApprovals([]);
      return;
    }
    setWaiterActionApprovals([]);

    const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
    const sessionIds = activeSessions.map((session) => String(session.sessionId));
    const loadWaiterActions = async () => {
      try {
        const rows: AdminApprovalRequestView[] = [];
        let pageNumber = 1;
        let total = 0;
        do {
          const page = await adminApprovalRequestsService.list({
            page: pageNumber,
            pageSize: WAITER_ACTION_PAGE_SIZE,
            session: sessionIds,
            status: ['approved'],
          });
          rows.push(...page.rows);
          total = page.total;
          pageNumber += 1;
        } while (rows.length < total);

        if (waiterActionQueryGenerationRef.current !== generation) return;
        setWaiterActionApprovals(rows
          .filter((row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases))
          .filter((row) => activeSessionIds.has(row.sessionId)));
      } catch {
        if (waiterActionQueryGenerationRef.current === generation) {
          setWaiterActionApprovals([]);
        }
      }
    };

    void loadWaiterActions();
  }, [activeSessions, approvalRevision, dashboardTenantRevision, tenantAliases, tenantId]);

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || activeSessions.length === 0) {
      setApprovalBaselineByBlockKey({});
      return () => { cancelled = true; };
    }

    setApprovalBaselineByBlockKey({});
    const comparisonBlocks = filterMode === 'range'
      ? [{
        key: 'range',
        comparison: computeSameDaysComparison(activeSessions, sessions, rangeStart, rangeEnd),
      }]
      : activeSessions.map((session) => ({
        key: `session-${session.sessionId}`,
        comparison: computeSameDaysComparison([session], sessions),
      }));

    void Promise.all(comparisonBlocks.map(async ({ key, comparison }) => {
      if (!comparison) {
        return [key, { approved: null, cancellations: null }] as const;
      }
      const query = {
        page: 1,
        pageSize: 1,
        session: comparison.sessionIds.map(String),
        status: ['approved'],
      };
      const [approvedPage, cancellationPage] = await Promise.all([
        adminApprovalRequestsService.list(query),
        adminApprovalRequestsService.list({ ...query, actionType: ['cancel'] }),
      ]);
      return [key, {
        approved: approvedPage.total,
        cancellations: cancellationPage.total,
      }] as const;
    })).then((entries) => {
      if (!cancelled) setApprovalBaselineByBlockKey(Object.fromEntries(entries));
    }).catch(() => {
      if (!cancelled) setApprovalBaselineByBlockKey({});
    });

    return () => { cancelled = true; };
  }, [activeSessions, approvalRevision, filterMode, rangeEnd, rangeStart, sessions, tenantId]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!tenantId || activeSessions.length === 0) {
      setReport(null);
      setReportsBySessionId({});
      setReportRequestFailed(false);
      setReportLoading(false);
      return;
    }

    const sessionIds = activeSessions.map((session) => session.sessionId);
    const loadReport = async () => {
      setReportLoading(true);
      try {
        const [reports, sessionReports] = await Promise.all([
          reportsService.getRange(tenantId, { sessionIds, aggregate: filterMode !== 'shift' }, undefined),
          filterMode === 'shift'
            ? Promise.resolve<ReportView[]>([])
            : reportsService.getRange(tenantId, { sessionIds, aggregate: false }, undefined),
        ]);
        const canonicalSessionReports = filterMode === 'shift' ? reports : sessionReports;
        setReport(reports[0] ?? null);
        setReportsBySessionId(Object.fromEntries(canonicalSessionReports
          .filter((row): row is ReportView & { sessionId: number } => typeof row.sessionId === 'number')
          .map((row) => [row.sessionId, row])));
        setReportRequestFailed(false);
      } catch {
        setReport(null);
        setReportsBySessionId({});
        setReportRequestFailed(true);
      } finally {
        setReportLoading(false);
      }
    };

    void loadReport();
  }, [activeSessions, approvalRevision, dashboardTenantRevision, filterMode, loading, tenantId]);

  useEffect(() => {
    if (!tenantId || activeSessions.length === 0) {
      setStationaryStationSummaries([]);
      setStationaryStationSummariesBySessionId({});
      return;
    }

    const sessionIds = activeSessions.map((session) => session.sessionId);
    const loadStationaryStationSummaries = async () => {
      try {
        setStationaryStationSummaries(await reportsService.getStationaryStationSummaries(tenantId, sessionIds, undefined));
        if (filterMode === 'shift') {
          const rows = await Promise.all(sessionIds.map(async (sessionId) => [
            sessionId,
            await reportsService.getStationaryStationSummaries(tenantId, [sessionId], undefined),
          ] as const));
          setStationaryStationSummariesBySessionId(Object.fromEntries(rows));
        } else {
          setStationaryStationSummariesBySessionId({});
        }
      } catch {
        setStationaryStationSummaries([]);
        setStationaryStationSummariesBySessionId({});
      }
    };

    void loadStationaryStationSummaries();
  }, [activeSessions, dashboardTenantRevision, filterMode, tenantId]);

  useEffect(() => {
    if (!tenantId || activeSessions.length === 0) {
      setCategorySales([]);
      setCategorySalesBySessionId({});
      setCategorySalesRequestFailed(false);
      return;
    }

    const sessionIds = activeSessions.map((session) => session.sessionId);
    const loadCategorySales = async () => {
      try {
        setCategorySales(await reportsService.getCategorySales(tenantId, sessionIds, undefined));
        if (filterMode === 'shift') {
          const rows = await Promise.all(sessionIds.map(async (sessionId) => [
            sessionId,
            await reportsService.getCategorySales(tenantId, [sessionId], undefined),
          ] as const));
          setCategorySalesBySessionId(Object.fromEntries(rows));
        } else {
          setCategorySalesBySessionId({});
        }
        setCategorySalesRequestFailed(false);
      } catch {
        setCategorySales([]);
        setCategorySalesBySessionId({});
        setCategorySalesRequestFailed(true);
      }
    };

    void loadCategorySales();
  }, [activeSessions, filterMode, tenantId]);

  useEffect(() => {
    if (!tenantId || activeSessions.length === 0) {
      setBestSellingProducts([]);
      setBestSellingProductsBySessionId({});
      setBestSellingRequestFailed(false);
      return;
    }

    const sessionIds = activeSessions.map((session) => session.sessionId);
    const loadBestSellingProducts = async () => {
      try {
        setBestSellingProducts(await reportsService.getBestSellingProducts(tenantId, sessionIds, undefined));
        if (filterMode === 'shift') {
          const rows = await Promise.all(sessionIds.map(async (sessionId) => [
            sessionId,
            await reportsService.getBestSellingProducts(tenantId, [sessionId], undefined),
          ] as const));
          setBestSellingProductsBySessionId(Object.fromEntries(rows));
        } else {
          setBestSellingProductsBySessionId({});
        }
        setBestSellingRequestFailed(false);
      } catch {
        setBestSellingProducts([]);
        setBestSellingProductsBySessionId({});
        setBestSellingRequestFailed(true);
      }
    };

    void loadBestSellingProducts();
  }, [activeSessions, filterMode, tenantId]);

  useEffect(() => {
    if (!tenantId || activeSessions.length === 0) {
      setMostCanceledProducts([]);
      setMostCanceledProductsBySessionId({});
      setMostCanceledRequestFailed(false);
      return;
    }

    const sessionIds = activeSessions.map((session) => session.sessionId);
    const loadMostCanceledProducts = async () => {
      try {
        setMostCanceledProducts(await reportsService.getMostCanceledProducts(tenantId, sessionIds, undefined));
        if (filterMode === 'shift') {
          const rows = await Promise.all(sessionIds.map(async (sessionId) => [
            sessionId,
            await reportsService.getMostCanceledProducts(tenantId, [sessionId], undefined),
          ] as const));
          setMostCanceledProductsBySessionId(Object.fromEntries(rows));
        } else {
          setMostCanceledProductsBySessionId({});
        }
        setMostCanceledRequestFailed(false);
      } catch {
        setMostCanceledProducts([]);
        setMostCanceledProductsBySessionId({});
        setMostCanceledRequestFailed(true);
      }
    };

    void loadMostCanceledProducts();
  }, [activeSessions, filterMode, tenantId]);

  useEffect(() => {
    if (!tenantId || activeSessions.length === 0) {
      setBumpTimesSummary({ firstBumpAt: null, lastBumpAt: null });
      setBumpTimesSummaryBySessionId({});
      setBumpTimesRequestFailed(false);
      return;
    }

    const sessionIds = activeSessions.map((session) => session.sessionId);
    const loadBumpTimes = async () => {
      try {
        setBumpTimesSummary(await reportsService.getBumpTimes(tenantId, sessionIds, undefined));
        if (filterMode === 'shift') {
          const rows = await Promise.all(sessionIds.map(async (sessionId) => [
            sessionId,
            await reportsService.getBumpTimes(tenantId, [sessionId], undefined),
          ] as const));
          setBumpTimesSummaryBySessionId(Object.fromEntries(rows));
        } else {
          setBumpTimesSummaryBySessionId({});
        }
        setBumpTimesRequestFailed(false);
      } catch {
        setBumpTimesSummary({ firstBumpAt: null, lastBumpAt: null });
        setBumpTimesSummaryBySessionId({});
        setBumpTimesRequestFailed(true);
      }
    };

    void loadBumpTimes();
  }, [activeSessions, filterMode, tenantId]);

  useEffect(() => {
    if (!tenantId || activeSessions.length === 0) {
      setSessionScopedPayments(null);
      return;
    }

    const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
    const sessionIds = activeSessions.map((session) => String(session.sessionId));
    if (sessionIds.length === 0) {
      setSessionScopedPayments(null);
      return;
    }

    const loadSessionScopedPayments = async () => {
      try {
        const rows: AdminPaymentlineView[] = [];
        let pageNumber = 1;
        let total = Infinity;
        while (rows.length < total) {
          const page = await adminPaymentsService.list({
            page: pageNumber,
            pageSize: 500,
            session: sessionIds,
            sortKey: 'updatedAt',
            sortDirection: 'desc',
          });
          rows.push(...page.rows);
          total = page.total;
          if (page.rows.length === 0) {
            break;
          }
          pageNumber += 1;
        }
        setSessionScopedPayments(
          rows
            .filter((row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases))
            .filter((row) => activeSessionIds.has(row.sessionId)),
        );
      } catch {
        setSessionScopedPayments(null);
      }
    };

    void loadSessionScopedPayments();
  }, [activeSessions, tenantAliases, tenantId]);

  const headerAnchorSession = activeSessions[0] ?? sessions[0] ?? null;

  useEffect(() => {
    setTotalsMode(headerAnchorSession?.state === 'open' ? 'ordersTotal' : 'closedShift');
  }, [headerAnchorSession]);

  const shownFilterMode = pendingStepRange ? 'range' : filterMode;
  const shownRangeStart = pendingStepRange?.[0] ?? rangeStart;
  const shownRangeEnd = pendingStepRange?.[1] ?? rangeEnd;
  const selectedDayEnd = shownFilterMode === 'range'
    ? shownRangeEnd ?? shownRangeStart
    : headerAnchorSession && new Date(headerAnchorSession.openTime);
  const cardAnchorDayMs = selectedDayEnd ? addDays(selectedDayEnd, 0).getTime() : null;
  const oldestSessionMs = sessions.length > 0 ? new Date(sessions[sessions.length - 1].openTime).getTime() : null;

  const anchorMonthLabel = useMemo(() => {
    if (cardAnchorDayMs === null) {
      return 'n/a';
    }
    return new Date(cardAnchorDayMs).toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
  }, [cardAnchorDayMs]);

  const anchorMonthSalesExTips = useMemo(() => {
    if (cardAnchorDayMs === null) {
      return 0;
    }
    const d = new Date(cardAnchorDayMs);
    // Sessions are the newest 200 rows, so a month older than the oldest loaded one is partial.
    if (oldestSessionMs !== null && oldestSessionMs > new Date(d.getFullYear(), d.getMonth(), 1).getTime()) {
      return null;
    }
    return sumWindowSales(
      sessions.filter((row) => {
        const candidate = new Date(row.openTime);
        return candidate.getFullYear() === d.getFullYear() && candidate.getMonth() === d.getMonth();
      }),
      synthesizedSessionIds,
    );
  }, [cardAnchorDayMs, oldestSessionMs, sessions, synthesizedSessionIds]);

  const anchorWeekStartMs = cardAnchorDayMs === null ? null : startOfIsraeliWeek(new Date(cardAnchorDayMs)).getTime();
  const anchorWeekLabel = anchorWeekStartMs === null ? 'n/a' : formatWeekLabel(new Date(anchorWeekStartMs));

  const anchorWeekSalesExTips = useMemo(() => {
    if (anchorWeekStartMs === null) {
      return 0;
    }
    if (oldestSessionMs !== null && oldestSessionMs > anchorWeekStartMs) {
      return null;
    }
    return sumWindowSales(
      sessions.filter((row) => startOfIsraeliWeek(new Date(row.openTime)).getTime() === anchorWeekStartMs),
      synthesizedSessionIds,
    );
  }, [anchorWeekStartMs, oldestSessionMs, sessions, synthesizedSessionIds]);

  const sameDaysComparison = useMemo(
    () => computeSameDaysComparison(
      activeSessions,
      sessions,
      filterMode === 'range' ? rangeStart : null,
      filterMode === 'range' ? rangeEnd : null,
    ),
    [activeSessions, filterMode, rangeEnd, rangeStart, sessions],
  );

  const selectedRevenue = report?.closedRevenue ?? sum(activeSessions.map((row) => row.salesTotal));
  const canonicalSessionRevenue = filterMode === 'shift' && activeSessions.length === 1 && report
    ? report.closedRevenue
    : null;
  const canonicalSessionId = canonicalSessionRevenue === null ? null : activeSessions[0].sessionId;
  const comparePct = sameDaysComparison && sameDaysComparison.baseline > 0
    ? ((selectedRevenue - sameDaysComparison.baseline) / sameDaysComparison.baseline) * 100
    : null;

  const sessionIdsSet = useMemo(() => new Set(activeSessions.map((row) => row.sessionId)), [activeSessions]);

  const paymentsInScope = useMemo(() => {
    if (sessionScopedPayments) {
      return sessionScopedPayments;
    }
    return payments.filter((row) => sessionIdsSet.has(row.sessionId));
  }, [payments, sessionIdsSet, sessionScopedPayments]);

  const closedOrderIdsKey = useMemo(
    () => [...new Map(
      paymentsInScope
        .filter((row) => row.status === 'done' && row.orderStatus === 'closed' && row.orderId > 0)
        .map((row) => [row.orderId, row.orderCode] as const),
    ).entries()]
      .map(([orderId, orderCode]) => `${orderId}:${orderCode}`)
      .sort()
      .join(','),
    [paymentsInScope],
  );

  const closedOrderDinersByCodeRef = useRef(closedOrderDinersByCode);
  closedOrderDinersByCodeRef.current = closedOrderDinersByCode;

  useEffect(() => {
    if (!tenantId) {
      setClosedOrderDinersByCode({});
      return;
    }

    if (!closedOrderIdsKey) {
      return;
    }

    const knownDiners = closedOrderDinersByCodeRef.current;
    const missing = closedOrderIdsKey
      .split(',')
      .map((entry) => {
        const [orderId, orderCode] = entry.split(':');
        return [Number(orderId), orderCode] as const;
      })
      .filter(([, orderCode]) => !knownDiners[orderCode]);

    if (missing.length === 0) {
      return;
    }

    let cancelled = false;
    const loadMissingDiners = async () => {
      const results = await Promise.allSettled(
        missing.map(async ([orderId, orderCode]) => {
          const details = await ordersService.getDashboardOrderDetails(tenantId, orderId);
          return { orderCode, dinersCount: details.order.dinersCount ?? 0 };
        }),
      );
      if (cancelled) {
        return;
      }
      setClosedOrderDinersByCode((current) => {
        const next = { ...current };
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            next[result.value.orderCode] = result.value.dinersCount;
          }
        });
        return next;
      });
    };

    void loadMissingDiners();
    return () => {
      cancelled = true;
    };
  }, [closedOrderIdsKey, tenantId]);

  const totalsByMode = useMemo(() => {
    if (filterMode === 'shift' && activeSessions.length === 1 && report) {
      const closedShift = report.closedRevenue;
      const openOrders = report.openOrdersTotal;
      const openShift = Math.max(0, report.salesWithOpenOrders - report.closedRevenue);
      const closedOrders = report.receiptsTotal;
      return { openShift, closedShift, openOrders, closedOrders };
    }

    const openShift = sum(activeSessions.filter((row) => row.state === 'open').map((row) => row.salesTotal));
    const closedShift = sum(activeSessions.filter((row) => row.state === 'closed').map((row) => row.salesTotal));
    const openOrders = sum(paymentsInScope.filter((row) => row.orderStatus === 'open' && row.status === 'done').map((row) => row.baseAmount));
    const closedOrders = sum(paymentsInScope.filter((row) => row.orderStatus === 'closed' && row.status === 'done').map((row) => row.baseAmount));
    return { openShift, closedShift, openOrders, closedOrders };
  }, [activeSessions, filterMode, paymentsInScope, report]);

  const salesBreakdownRows = useMemo<BreakdownRow[]>(() => {
    if (!report) {
      return [];
    }
    return [
      { key: 'Food', amount: report.salesByReportGroup.food },
      { key: 'Beverages', amount: report.salesByReportGroup.beverages },
      { key: 'Other', amount: report.salesByReportGroup.other },
      { key: 'Tips', amount: report.tipsTotal },
    ];
  }, [report]);

  const filteredBreakdown = useMemo(() => {
    const needle = breakdownQuery.trim().toLowerCase();
    if (!needle) {
      return salesBreakdownRows;
    }
    return salesBreakdownRows.filter((row) => row.key.toLowerCase().includes(needle));
  }, [breakdownQuery, salesBreakdownRows]);

  const approvalRows = useMemo(() => {
    return {
      rows: approvals,
      hasMore: approvals.length < approvalTotal,
      totalRows: approvalTotal,
    };
  }, [approvalTotal, approvals]);

  const loadApprovalExportSource = useCallback(async (): Promise<{
    rows: AdminApprovalRequestView[];
    detailsById: Record<number, AdminApprovalRequestDetailView>;
  }> => {
    if (!tenantId || activeSessions.length === 0) {
      return { rows: [], detailsById: {} };
    }

    const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
    const sessionIds = activeSessions.map((session) => String(session.sessionId));
    const rows: AdminApprovalRequestView[] = [];

    let pageNumber = 1;
    let total = Infinity;
    while (rows.length < total) {
      const page = await adminApprovalRequestsService.list({
        page: pageNumber,
        pageSize: 500,
        session: sessionIds,
      });
      rows.push(...page.rows);
      total = page.total;
      if (page.rows.length === 0) {
        break;
      }
      pageNumber += 1;
    }

    const scopedRows = rows
      .filter((row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases))
      .filter((row) => activeSessionIds.has(row.sessionId))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const detailsById: Record<number, AdminApprovalRequestDetailView> = { ...approvalDetailsById };
    const missingIds = scopedRows
      .map((row) => row.id)
      .filter((id) => !(id in detailsById));

    if (missingIds.length > 0) {
      const detailResults = await Promise.allSettled(missingIds.map((id) => adminApprovalRequestsService.getDetail(id)));
      const freshDetails: Record<number, AdminApprovalRequestDetailView> = {};
      detailResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const id = missingIds[index];
          freshDetails[id] = result.value;
          detailsById[id] = result.value;
        }
      });
      if (Object.keys(freshDetails).length > 0) {
        setApprovalDetailsById((current) => ({ ...current, ...freshDetails }));
      }
    }

    return { rows: scopedRows, detailsById };
  }, [activeSessions, approvalDetailsById, tenantAliases, tenantId]);

  const loadMoreApprovalRows = useCallback(() => {
    if (!tenantId || requestedApprovalMode !== approvalMode || approvalLoadInFlightRef.current || approvals.length >= approvalTotal || activeSessions.length === 0) {
      return;
    }
    approvalLoadInFlightRef.current = true;
    const generation = approvalQueryGenerationRef.current;
    const nextPage = approvalPage + 1;
    const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
    const sessionIds = activeSessions.map((session) => String(session.sessionId));
    void adminApprovalRequestsService.list({
      page: nextPage,
      pageSize: APPROVAL_PAGE_SIZE,
      session: sessionIds,
      status: approvalMode === 'all' ? undefined : [approvalMode],
    }).then((page) => {
      if (approvalQueryGenerationRef.current !== generation) return;
      const nextRows = page.rows
        .filter((row) => isSameTenant(row.tenantId, tenantId) || doesTenantAliasMatch(row.tenantId, tenantAliases))
        .filter((row) => activeSessionIds.has(row.sessionId));
      setApprovals((previous) => [
        ...previous,
        ...nextRows.filter((row) => !previous.some((existing) => existing.id === row.id)),
      ]);
      setApprovalPage(nextPage);
      setApprovalTotal(page.total);
    }).catch(() => {
      // Keep the current page visible; the next scroll retries this page.
    }).finally(() => {
      if (approvalQueryGenerationRef.current === generation) {
        approvalLoadInFlightRef.current = false;
      }
    });
  }, [activeSessions, approvalMode, approvalPage, approvalTotal, approvals.length, requestedApprovalMode, tenantAliases, tenantId]);

  useEffect(() => {
    const missingIds = [...new Set([...approvals, ...waiterActionApprovals]
      .map((row) => row.id))]
      .filter((id) => !(id in approvalDetailsById));
    if (missingIds.length === 0) {
      return;
    }

    let cancelled = false;
    const loadDetails = async () => {
      const results = await Promise.allSettled(missingIds.map((id) => adminApprovalRequestsService.getDetail(id)));
      if (cancelled) {
        return;
      }
      setApprovalDetailsById((current) => {
        const next = { ...current };
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            next[missingIds[index]] = result.value;
          }
        });
        return next;
      });
    };

    void loadDetails();
    return () => {
      cancelled = true;
    };
  }, [approvalDetailsById, approvals, waiterActionApprovals]);

  const paymentByMethod = useMemo(
    () => computePaymentByMethod(paymentsInScope, report),
    [paymentsInScope, report],
  );

  const graphRows = useMemo<BarRow[]>(() => computeGraphRows(paymentsInScope), [paymentsInScope]);

  const closedOrdersSummary = useMemo(
    () => computeClosedOrdersSummary(paymentsInScope, report),
    [paymentsInScope, report],
  );

  const openOrdersSummary = useMemo(
    () => computeOpenOrdersSummary(paymentsInScope, report),
    [paymentsInScope, report],
  );

  const cancellationTotals = useMemo(
    () => computeCancellationTotals(cancelDetails, report),
    [cancelDetails, report],
  );

  const waiterIssues = useMemo(() => {
    const map = new Map<string, number>();
    approvals.forEach((row) => {
      if (row.reason === 'waiter_error' || row.reason === 'manager_action' || row.reason === 'cancel_order') {
        map.set(row.requestedByName, (map.get(row.requestedByName) ?? 0) + 1);
      }
    });
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [approvals]);

  const waiterActionBreakdown = useMemo(
    () => computeWaiterActionBreakdown(waiterActionApprovals, report?.discountsAmount),
    [report?.discountsAmount, waiterActionApprovals],
  );

  const onLoadPreviousShift = () => {
    if (filterMode !== 'shift') {
      return;
    }

    const orderedSessions = [...sessions].sort((a, b) => new Date(b.openTime).getTime() - new Date(a.openTime).getTime());

    if (loadedSessionIds.length === 0 && orderedSessions.length > 0) {
      setLoadedSessionIds([orderedSessions[0].sessionId]);
      return;
    }

    const known = new Set(loadedSessionIds);
    const loadedIndexes = loadedSessionIds
      .map((sessionId) => orderedSessions.findIndex((row) => row.sessionId === sessionId))
      .filter((index) => index >= 0);
    const oldestLoadedIndex = loadedIndexes.length > 0 ? Math.max(...loadedIndexes) : -1;
    const next = orderedSessions.slice(oldestLoadedIndex + 1).find((row) => !known.has(row.sessionId));
    if (!next) {
      return;
    }
    isManualSessionSelectionRef.current = true;
    cancelPendingStep();
    setLoadedSessionIds((previous) => [...previous, next.sessionId].slice(-3));
  };

  const isShiftOpen = headerAnchorSession?.state === 'open';

  const totalsCards = computeTotalsCards(isShiftOpen, closedOrdersSummary, openOrdersSummary, totalsByMode.closedShift);

  const trendSignClass = comparePct !== null && comparePct < 0 ? 'down' : 'up';
  const headerBlockKey = filterMode === 'range' ? 'range' : (headerAnchorSession ? `session-${headerAnchorSession.sessionId}` : '');
  const headerApprovalBaseline = approvalBaselineByBlockKey[headerBlockKey];
  const topKpiRows = [
    { label: 'ביטולים', pct: computeCountComparisonPct(waiterActionApprovals.filter((row) => row.actionType === 'cancel').length, headerApprovalBaseline?.cancellations ?? null) },
    { label: 'פעולות מנהל', pct: computeCountComparisonPct(waiterActionApprovals.length, headerApprovalBaseline?.approved ?? null) },
  ];

  const dashboardBlocks = useMemo<DashboardSessionBlock[]>(() => {
    const buildBlock = (
      blockSessions: AdminSessionAuditView[],
      blockReport: ReportView | null,
      blockLiveOrders: OrderView[],
      blockApprovals: AdminApprovalRequestView[],
      blockWaiterActions: AdminApprovalRequestView[],
      blockPayments: AdminPaymentlineView[],
      blockCancelDetails: AdminApprovalRequestDetailView[],
      blockCategorySales: CategorySales[],
      blockBestSellingProducts: BestSellingProduct[],
      blockMostCanceledProducts: MostCanceledProduct[],
      blockStationaryStationSummaries: ReportStationaryStationSummary[],
      blockBumpTimesSummary: BumpTimesSummary,
      key: string,
    ): DashboardSessionBlock => {
      const anchorSession = blockSessions[0] ?? null;
      const blockRevenue = blockReport?.closedRevenue ?? sum(blockSessions.map((row) => row.salesTotal));
      const blockSameDaysComparison = computeSameDaysComparison(
        blockSessions,
        sessions,
        key === 'range' ? rangeStart : null,
        key === 'range' ? rangeEnd : null,
      );
      const blockComparePct = blockSameDaysComparison && blockSameDaysComparison.baseline > 0
        ? ((blockRevenue - blockSameDaysComparison.baseline) / blockSameDaysComparison.baseline) * 100
        : null;
      const blockTrendSignClass = blockComparePct !== null && blockComparePct < 0 ? 'down' : 'up';
      const blockPaymentByMethod = computePaymentByMethod(blockPayments, blockReport);
      const blockGraphRows = computeGraphRows(blockPayments);
      const blockClosedOrdersSummary = computeClosedOrdersSummary(blockPayments, blockReport);
      const blockOpenOrdersSummary = computeOpenOrdersSummary(blockPayments, blockReport);
      const blockCancellationTotals = computeCancellationTotals(blockCancelDetails, blockReport);
      const blockApprovalBaseline = approvalBaselineByBlockKey[key];
      const blockIsOpen = anchorSession?.state === 'open';
      const blockTotalsCards = computeTotalsCards(
        blockIsOpen,
        blockClosedOrdersSummary,
        blockOpenOrdersSummary,
        blockReport?.closedRevenue ?? sum(blockSessions.map((row) => row.salesTotal)),
      );
      return {
        key,
        session: anchorSession,
        report: blockReport,
        selectedRevenue: blockRevenue,
        activeSessions: blockSessions,
        liveOrders: blockLiveOrders,
        closedOrderDinersByCode,
        payments: blockPayments,
        reportsBySessionId,
        paymentByMethod: blockPaymentByMethod,
        graphRows: blockGraphRows,
        closedOrdersSummary: blockClosedOrdersSummary,
        cancellationTotals: blockCancellationTotals,
        cancelDetails: blockCancelDetails,
        waiterActionBreakdown: computeWaiterActionBreakdown(blockWaiterActions, blockReport?.discountsAmount),
        waiterActionRows: blockWaiterActions,
        categorySales: blockCategorySales,
        bestSellingProducts: blockBestSellingProducts,
        mostCanceledProducts: blockMostCanceledProducts,
        stationaryStationSummaries: blockStationaryStationSummaries,
        bumpTimesSummary: blockBumpTimesSummary,
        trendSignClass: blockTrendSignClass,
        sameDaysComparison: blockSameDaysComparison,
        comparePct: blockComparePct,
        totalsCards: blockTotalsCards,
        topKpiRows: [
          { label: 'ביטולים', pct: computeCountComparisonPct(blockWaiterActions.filter((row) => row.actionType === 'cancel').length, blockApprovalBaseline?.cancellations ?? null) },
          { label: 'פעולות מנהל', pct: computeCountComparisonPct(blockWaiterActions.length, blockApprovalBaseline?.approved ?? null) },
        ],
        approvalRows: {
          rows: blockApprovals,
          hasMore: blockApprovals.length < approvalTotal,
          totalRows: approvalTotal,
        },
      };
    };

    if (filterMode !== 'shift') {
      return [buildBlock(
        activeSessions,
        report,
        openOrders.filter((row) => sessionIdsSet.has(row.sessionId)),
        approvals,
        waiterActionApprovals,
        paymentsInScope,
        cancelDetails,
        categorySales,
        bestSellingProducts,
        mostCanceledProducts,
        stationaryStationSummaries,
        bumpTimesSummary,
        'range',
      )];
    }

    return activeSessions.map((session) => {
      const sessionApprovals = approvals.filter((row) => row.sessionId === session.sessionId);
      const sessionWaiterActions = waiterActionApprovals.filter((row) => row.sessionId === session.sessionId);
      return buildBlock(
        [session],
        reportsBySessionId[session.sessionId] ?? (activeSessions.length === 1 ? report : null),
        openOrders.filter((row) => row.sessionId === session.sessionId),
        sessionApprovals,
        sessionWaiterActions,
        paymentsInScope.filter((row) => row.sessionId === session.sessionId),
        cancelDetails.filter((detail) => detail.sessionId === session.sessionId),
        categorySalesBySessionId[session.sessionId] ?? (activeSessions.length === 1 ? categorySales : []),
        bestSellingProductsBySessionId[session.sessionId] ?? (activeSessions.length === 1 ? bestSellingProducts : []),
        mostCanceledProductsBySessionId[session.sessionId] ?? (activeSessions.length === 1 ? mostCanceledProducts : []),
        stationaryStationSummariesBySessionId[session.sessionId] ?? (activeSessions.length === 1 ? stationaryStationSummaries : []),
        bumpTimesSummaryBySessionId[session.sessionId] ?? (activeSessions.length === 1 ? bumpTimesSummary : { firstBumpAt: null, lastBumpAt: null }),
        `session-${session.sessionId}`,
      );
    });
  }, [
    activeSessions,
    approvalMode,
    approvalTotal,
    approvals,
    waiterActionApprovals,
    openOrders,
    bestSellingProducts,
    bestSellingProductsBySessionId,
    bumpTimesSummary,
    bumpTimesSummaryBySessionId,
    cancelDetails,
    categorySales,
    categorySalesBySessionId,
    filterMode,
    mostCanceledProducts,
    mostCanceledProductsBySessionId,
    stationaryStationSummaries,
    stationaryStationSummariesBySessionId,
    approvalBaselineByBlockKey,
    paymentsInScope,
    rangeEnd,
    rangeStart,
    report,
    reportsBySessionId,
    closedOrderDinersByCode,
    sessionIdsSet,
    sessions,
  ]);

  const formatRangeDate = (value: Date) => value.toLocaleDateString('he-IL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const formatRangeText = (start: Date, end: Date) => {
    const [startDate, endDate] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();
    const startDay = startDate.toLocaleDateString('he-IL', { day: 'numeric' });
    const endDay = endDate.toLocaleDateString('he-IL', { day: 'numeric' });
    const startMonth = startDate.toLocaleDateString('he-IL', { month: 'long' });
    const endMonth = endDate.toLocaleDateString('he-IL', { month: 'long' });
    const startYear = startDate.toLocaleDateString('he-IL', { year: 'numeric' });
    const endYear = endDate.toLocaleDateString('he-IL', { year: 'numeric' });

    if (sameMonth && startDay !== endDay) {
      return `${startDay}-${endDay} ב${startMonth} ${startYear}`;
    }

    if (sameYear) {
      return `${startDay} ב${startMonth} - ${endDay} ב${endMonth} ${startYear}`;
    }

    return `${startDay} ב${startMonth} ${startYear} - ${endDay} ב${endMonth} ${endYear}`;
  };

  const headerDateText = shownFilterMode === 'range'
    ? (() => {
      if (shownRangeStart && shownRangeEnd) {
        const startText = formatRangeDate(shownRangeStart);
        const endText = formatRangeDate(shownRangeEnd);
        return startText === endText ? formatHeaderDay(shownRangeStart) : formatRangeText(shownRangeStart, shownRangeEnd);
      }
      if (shownRangeStart) {
        return formatRangeDate(shownRangeStart);
      }
      if (shownRangeEnd) {
        return formatRangeDate(shownRangeEnd);
      }
      return 'לא נבחר טווח תאריכים';
    })()
    : headerAnchorSession
      ? formatHeaderDay(new Date(headerAnchorSession.openTime))
      : 'לא נטענו משמרות';

  const runApprovalDecision = async (requestId: number, decision: 'approved' | 'denied'): Promise<'ok' | 'already-decided' | 'error'> => {
    if (!tenantId) {
      return 'error';
    }
    if (approvalDecisionInFlightRef.current) {
      return 'error';
    }
    const selectedApprover = managerOptions.find((manager) => manager.fullName === selectedApproverName);
    if (!selectedApprover) {
      setApprovalsActionError('Choose approver name first.');
      return 'error';
    }
    approvalDecisionInFlightRef.current = true;
    setApprovalsActionBusy(true);
    setApprovalsActionError(null);
    try {
      await approvalsService.decideAsAdmin(tenantId, requestId, decision, selectedApprover.id);
      const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
      const sessionIds = activeSessions.map((session) => String(session.sessionId));
      const refreshed = await adminApprovalRequestsService.list({
        page: 1,
        pageSize: APPROVAL_PAGE_SIZE,
        session: sessionIds,
        status: approvalMode === 'all' ? undefined : [approvalMode],
      });
      setApprovals(
        refreshed.rows
          .filter((row) => row.tenantId === tenantId)
          .filter((row) => activeSessionIds.has(row.sessionId))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      );
      setApprovalPage(1);
      setApprovalTotal(refreshed.total);
      setApprovalDetailsById((current) => {
        const next = { ...current };
        delete next[requestId];
        return next;
      });
      return 'ok';
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'APPROVAL_REQUEST_NOT_FOUND_OR_NOT_PENDING') {
        const activeSessionIds = new Set(activeSessions.map((session) => session.sessionId));
        const sessionIds = activeSessions.map((session) => String(session.sessionId));
        const refreshed = await adminApprovalRequestsService.list({
          page: 1,
          pageSize: APPROVAL_PAGE_SIZE,
          session: sessionIds,
          status: approvalMode === 'all' ? undefined : [approvalMode],
        });
        setApprovals(
          refreshed.rows
            .filter((row) => row.tenantId === tenantId)
            .filter((row) => activeSessionIds.has(row.sessionId))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        );
        setApprovalPage(1);
        setApprovalTotal(refreshed.total);
        return 'already-decided';
      }
      setApprovalsActionError(err instanceof Error ? err.message : 'Approval update failed');
      return 'error';
    } finally {
      setApprovalsActionBusy(false);
      approvalDecisionInFlightRef.current = false;
    }
  };

  const toggleSection = (section: SectionKey) => {
    setCollapsedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const setDateRange = ([start, end]: [Date | null, Date | null]) => {
    cancelPendingStep();
    setRangeStart(start);
    setRangeEnd(end);
  };

  const selectFilterMode = (mode: FilterMode) => {
    cancelPendingStep();
    setFilterMode(mode);
  };

  const canStepToNextDay = !!selectedDayEnd && addDays(selectedDayEnd, 0) < addDays(new Date(), 0);

  const commitPendingStep = useCallback(() => {
    stepCommitTimerRef.current = null;
    const pending = pendingStepRangeRef.current;
    if (!pending) {
      return;
    }
    pendingStepRangeRef.current = null;
    setPendingStepRange(null);
    setRangeStart(pending[0]);
    setRangeEnd(pending[1]);
    setFilterMode('range');
  }, []);

  useEffect(() => () => {
    if (stepCommitTimerRef.current) {
      clearTimeout(stepCommitTimerRef.current);
    }
    commitPendingStep();
  }, [commitPendingStep]);

  // Summary: Moves the shown day by delta at once; commits the range after 300ms idle.
  const stepSelectedDay = (delta: number) => {
    const day = selectedDayEnd ?? new Date();
    const [from, to] = pendingStepRangeRef.current
      ?? (filterMode === 'range' && rangeStart && rangeEnd ? [rangeStart, rangeEnd] : [day, day]);
    const next: [Date, Date] = [addDays(from, delta), addDays(to, delta)];
    pendingStepRangeRef.current = next;
    setPendingStepRange(next);
    if (stepCommitTimerRef.current) {
      clearTimeout(stepCommitTimerRef.current);
    }
    stepCommitTimerRef.current = setTimeout(commitPendingStep, 300);
  };

  const searchBestSellingProducts = useCallback(
    (sessionIds: number[], search: string): Promise<BestSellingProduct[]> => {
      if (!tenantId || sessionIds.length === 0) {
        return Promise.resolve([]);
      }
      return reportsService.getBestSellingProducts(tenantId, sessionIds, undefined, search);
    },
    [tenantId],
  );

  const reportDataFailureSections = useMemo(() => {
    const sections: string[] = [];
    if (reportRequestFailed) {
      sections.push('מכירות', 'תשלומים');
    }
    if (categorySalesRequestFailed) {
      sections.push('קטגוריות');
    }
    if (bestSellingRequestFailed) {
      sections.push('הנמכרים ביותר');
    }
    if (mostCanceledRequestFailed) {
      sections.push('המבוטלים ביותר');
    }
    if (bumpTimesRequestFailed) {
      sections.push('זמני בון');
    }
    return [...new Set(sections)];
  }, [
    bestSellingRequestFailed,
    bumpTimesRequestFailed,
    categorySalesRequestFailed,
    mostCanceledRequestFailed,
    reportRequestFailed,
  ]);

  return {
    loading: isFetchingCoreData && !initialLoadSettledRef.current,
    error,
    sessions,
    approvals,
    report,
    categorySales,
    filterMode,
    setFilterMode: selectFilterMode,
    rangeStart,
    rangeEnd,
    setDateRange,
    loadedSessionIds,
    setLoadedSessionIds: selectLoadedSessionIds,
    totalsMode,
    setTotalsMode,
    breakdownQuery,
    setBreakdownQuery,
    approvalMode,
    setApprovalMode: setRequestedApprovalMode,
    loadMoreApprovalRows,
    loadApprovalExportSource,
    isFilterDialogOpen,
    setIsFilterDialogOpen,
    pendingSessionId,
    setPendingSessionId,
    selectedBreakdownRow,
    setSelectedBreakdownRow,
    approvalsActionBusy,
    managerOptions,
    selectedApproverName,
    setSelectedApproverName,
    approvalsActionError,
    collapsedSections,
    toggleSection,
    tenantSessionOptions,
    activeSessions,
    dashboardBlocks,
    headerAnchorSession,
    anchorMonthLabel,
    anchorMonthSalesExTips,
    anchorWeekLabel,
    anchorWeekSalesExTips,
    canStepToNextDay,
    stepSelectedDay,
    sameDaysComparison,
    selectedRevenue,
    canonicalSessionId,
    canonicalSessionRevenue,
    comparePct,
    paymentsInScope,
    totalsByMode,
    filteredBreakdown,
    approvalRows,
    approvalDetailsById,
    paymentByMethod,
    graphRows,
    closedOrdersSummary,
    cancellationTotals,
    cancelDetails,
    waiterIssues,
    waiterActionBreakdown,
    bestSellingProducts,
    mostCanceledProducts,
    bumpTimesSummary,
    onLoadPreviousShift,
    runApprovalDecision,
    payments,
    trendSignClass,
    topKpiRows,
    isShiftOpen,
    totalsCards,
    headerDateText,
    reportDataFailureSections,
    searchBestSellingProducts,
  };
}
